import React from "react";

import type { MetricQuery } from "~/models/metric-query";
import type { Time } from "~/tools/date-time/time";
import { toFormatterUnit } from "~/tools/formatter-unit";
import { type Impulse, isImpulse, useScoped } from "~/tools/impulse";
import { isError } from "~/tools/is-error";
import { isPresent } from "~/tools/is-present";
import type { JSON } from "~/tools/json";
import { prop } from "~/tools/prop";
import { RemoteData } from "~/tools/remote-data";

import { SpecificRange, type TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { calculateQueryRangeStep } from "../../../metrics/utils";
import { queryRangePromQL } from "../../../queries/metrics/batch";
import { type RetryValue, type UseFullQueryOptions, useQueries } from "../../../queries/react-query";
import { TimeSeriesChart, type TimeSeriesChartProps } from "../../TimeSeriesChart";
import type { TimeSeries } from "../../TimeSeriesChart/time-series";
import { EmptyChart } from "../chart/chart-states/EmptyChart";
import { ErrorChart } from "../chart/chart-states/ErrorChart";
import { LoadingChart } from "../chart/chart-states/LoadingChart";

import { useViewBox } from "./_viewbox-context";

export interface ThumbnailChartContainerProps
	extends Pick<
		TimeSeriesChartProps,
		"yAxis" | "legend" | "markerTimestamp" | "type" | "connectNulls" | "thresholdLines"
	> {
	batch: JSON;
	namespace?: JSON;
	width: number;

	unit: undefined | string;

	metricQueries: ReadonlyArray<MetricQuery>;
	/**
	 * When Impulse, the chart is interactive - a user can brush it and the impulse updates its value
	 */
	timeRange: TimeRange | Impulse<TimeRange>;
	retry?: RetryValue<Error>;
	onOpenMetricsDrawer: VoidFunction;
}

export const ThumbnailChartContainer: React.FC<ThumbnailChartContainerProps> = ({
	batch,
	namespace,
	unit,
	metricQueries,
	timeRange: timeRangeFromProps,
	yAxis,
	legend,
	width,
	retry,
	onOpenMetricsDrawer,
	markerTimestamp,
	type,
	connectNulls,
	thresholdLines,
}) => {
	const viewBoxElement = useViewBox();
	const timeRange = useScoped(
		(scope) => (isImpulse(timeRangeFromProps) ? timeRangeFromProps.getValue(scope) : timeRangeFromProps),
		[timeRangeFromProps],
	);

	const defaultStep = calculateQueryRangeStep({ timeRange, chartWidth: width });

	const results = useQueries(
		metricQueries.map((metricQuery) => {
			const step = metricQuery.step ?? defaultStep;

			return {
				enabled: Boolean(step) && metricQuery.promql.length > 0,
				namespace,
				retry,
				refetchOnTimelineSummaryUpdate: true,

				...queryRangePromQL({
					batch,
					query: metricQuery.promql,
					timeRange,
					step,
				}),
			} satisfies UseFullQueryOptions;
		}),
	);

	if (metricQueries.length === 0) {
		return <EmptyChart size="lg" />;
	}

	const error = results.map(prop("error")).find(isPresent);

	if (isError(error)) {
		return <ErrorChart error={error} size="lg" />;
	}

	if (error != null) {
		return <ErrorChart error={new Error("Unknown error")} size="lg" />;
	}

	return RemoteData.all(results.map(({ data = RemoteData.loading() }) => data)).match({
		loading: () => <LoadingChart size="lg" />,

		failure: ({ error: message }) => <ErrorChart error={new Error(message)} size="lg" />,

		success: ({ value }) => {
			const timeSeries = value.flatMap((series, index) => {
				const metricQuery = metricQueries.at(index);

				return series.map(
					(matrix): TimeSeries => ({
						...matrix,
						alias: metricQuery?.label,
						componentIdentifier: metricQuery?.componentIdentifier,
					}),
				);
			});

			if (timeSeries.length === 0) {
				return <EmptyChart size="lg" />;
			}

			return (
				<TimeSeriesChart
					canvasHeight={200}
					connectNulls={connectNulls}
					legend={legend}
					markerTimestamp={markerTimestamp}
					maxVisibleTimeSeries={{
						count: 5,
						onShowAll: onOpenMetricsDrawer,
					}}
					onBrush={
						isImpulse(timeRangeFromProps)
							? (start: Time, end: Time) => {
									timeRangeFromProps.setValue(new SpecificRange(start, end));
								}
							: undefined
					}
					thresholdLines={thresholdLines}
					timeRange={timeRange}
					timeSeries={timeSeries}
					tooltipViewBoxElement={viewBoxElement}
					type={type}
					unit={toFormatterUnit(unit)}
					yAxis={yAxis}
				/>
			);
		},
	});
};
